import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  Bell,
  ChevronRight,
  ClipboardList,
  Clock3,
  Copy,
  FileText,
  LogOut,
  Package2,
  ShieldCheck,
  Truck,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import useCustomerAppSession from "@/hooks/useCustomerAppSession";
import { usePublicSettings } from "@/hooks/usePublicSiteData";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { STATUS_LABEL } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SHIPPING_MODES = {
  sea: {
    label: "Sea freight",
    ratePerKg: 0.85,
    minimum: 180,
    serviceFee: 45,
    eta: "24-40 days",
  },
  air: {
    label: "Air cargo",
    ratePerKg: 4.8,
    minimum: 140,
    serviceFee: 55,
    eta: "6-12 days",
  },
  express: {
    label: "Express courier",
    ratePerKg: 8.5,
    minimum: 95,
    serviceFee: 35,
    eta: "3-7 days",
  },
};

const ORIGIN_COUNTRIES = {
  bangladesh: { label: "Bangladesh", multiplier: 0.95, exportDocs: 24 },
  china: { label: "China", multiplier: 1.1, exportDocs: 38 },
  europe: { label: "Europe", multiplier: 1.24, exportDocs: 48 },
  india: { label: "India", multiplier: 0.98, exportDocs: 28 },
};

const DESTINATION_PORTS = {
  chittagong: {
    label: "Chittagong Port",
    market: "Bangladesh",
    multiplier: 1.08,
    localCharge: 95,
  },
  mongla: {
    label: "Mongla Port",
    market: "Bangladesh",
    multiplier: 1.03,
    localCharge: 82,
  },
  rotterdam: {
    label: "Port of Rotterdam",
    market: "Europe",
    multiplier: 1.18,
    localCharge: 136,
  },
  dubai: {
    label: "Jebel Ali Port",
    market: "Middle East",
    multiplier: 1.1,
    localCharge: 110,
  },
};

const HS_CODE_PROFILES = {
  surgical: {
    label: "HS 9018.90 - Surgical & medical devices",
    dutyRate: 7.5,
    taxRate: 15,
  },
  automation: {
    label: "HS 8537.10 - Industrial automation controls",
    dutyRate: 10,
    taxRate: 12,
  },
  stainless: {
    label: "HS 7306.40 - Stainless steel supply",
    dutyRate: 12,
    taxRate: 15,
  },
  textile: {
    label: "HS 5208.52 - Cotton textile goods",
    dutyRate: 8,
    taxRate: 10,
  },
};

const CURRENCY_RATES = {
  USD: 1,
  BDT: 122,
  CNY: 7.18,
};

const MILESTONE_STEPS = [
  { key: "confirmed", label: "Order Confirmed" },
  { key: "production", label: "Production" },
  { key: "loading", label: "Port of Loading" },
  { key: "transit", label: "Ocean/Air Transit" },
  { key: "customs", label: "Customs Clearance" },
  { key: "delivered", label: "Delivered" },
];

function scrollToId(id) {
  if (typeof document === "undefined") return;
  document.getElementById(id)?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatDateShort(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}

function parsePositive(value) {
  const next = Number(value);
  return Number.isFinite(next) && next > 0 ? next : 0;
}

function formatMoney(value, currency = "USD") {
  return new Intl.NumberFormat(currency === "BDT" ? "bn-BD" : "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);
}

function convertMoney(value, currency) {
  return value * (CURRENCY_RATES[currency] || 1);
}

function copyField(value, label) {
  if (!value || typeof navigator === "undefined" || !navigator.clipboard) return;
  navigator.clipboard.writeText(String(value)).then(
    () => toast.success(`${label} copied`),
    () => {}
  );
}

function shipmentStepIndex(shipment) {
  const status = shipment?.current_status;
  if (status === "sourced") return 0;
  if (status === "processing") return 1;
  if (status === "in_transit") return 3;
  if (status === "customs") return 4;
  if (status === "out_for_delivery") return 4;
  if (status === "delivered") return 5;
  if (status === "delayed") return 3;
  return 0;
}

function etdForShipment(shipment) {
  const firstEvent = shipment?.events?.[0]?.timestamp;
  return firstEvent || shipment?.created_at || "";
}

function etaForShipment(shipment) {
  if (shipment?.eta_start && shipment?.eta_end) {
    return `${formatDateShort(shipment.eta_start)} - ${formatDateShort(shipment.eta_end)}`;
  }
  return shipment?.eta || "Awaiting carrier confirmation";
}

function PortalHero({
  customer,
  orders,
  shipments,
  onLogout,
  onOpenMarketplace,
  signedIn,
  language,
  setLanguage,
  alertCount,
}) {
  return (
    <div className="overflow-hidden rounded-[34px] border border-white/60 bg-[linear-gradient(145deg,rgba(16,23,44,0.98),rgba(42,19,65,0.96))] p-8 text-white shadow-[0_36px_90px_-46px_rgba(10,13,28,0.86)] sm:p-10">
      <div className="flex justify-end">
        <div className="flex items-center gap-2">
          <select
            value={language}
            onChange={(event) => setLanguage(event.target.value)}
            className="h-10 rounded-full border border-white/12 bg-white/10 px-4 text-sm text-white outline-none backdrop-blur"
            aria-label="Portal language"
          >
            <option value="en" className="text-gray-900">EN</option>
            <option value="zh-CN" className="text-gray-900">中文</option>
          </select>
          <button
            type="button"
            onClick={() => scrollToId("portal-shipments")}
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-white/10 text-white transition hover:bg-white/15"
            aria-label="Portal notifications"
          >
            <Bell className="h-4 w-4" />
            <span className="absolute -right-1 -top-1 grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-[var(--rsi-green)] px-1 text-[10px] font-bold text-white">
              {alertCount}
            </span>
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-8 xl:grid-cols-[minmax(0,1.05fr)_minmax(320px,0.95fr)] xl:items-end">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white/78">
            <ShieldCheck className="h-3.5 w-3.5" />
            Client Supply Chain Portal
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
            {signedIn
              ? "A modern trade dashboard built for serious import planning."
              : "Welcome to a smarter customer portal built to make your sourcing journey easier, safer, and more confident."}
          </h1>
          <p className="mt-4 max-w-3xl text-sm sm:text-base leading-7 text-white/82">
            {signedIn
              ? "Review your own trade records, follow shipment progress, estimate landed cost with freight-style breakdown, and access the document flow from one customer-facing portal."
              : "Explore the planner below, preview your landed cost clearly, and see how professionally your orders and shipment records can be managed in one place. When you sign in with your customer Access ID and Access Code, your private workspace opens with secure access to your own trade information, planning tools, and support flow."}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {signedIn ? (
              <>
                <button type="button" onClick={onOpenMarketplace} className="btn-outline-light">
                  Open app marketplace
                </button>
                <button type="button" onClick={onLogout} className="btn-outline-light">
                  <LogOut className="h-4 w-4" /> Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/#cta-strip" className="btn-primary">
                  Open home portal <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/app#customer-workspace" className="btn-outline-light">
                  Open app workspace sign-in
                </Link>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="metric-tile !bg-white/8 !border-white/10 text-white">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/56">Customer</div>
            <div className="mt-3 text-lg font-bold text-white">
              {signedIn ? customer?.customer_name || "Customer" : "Protected buyer access"}
            </div>
            <div className="mt-2 text-xs leading-6 text-white/72">
              {signedIn ? customer?.company_name || "Buyer profile linked" : "Each customer sees only their own linked records."}
            </div>
          </div>
          <div className="metric-tile !bg-white/8 !border-white/10 text-white">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/56">Access</div>
            <div className="mt-3 text-lg font-bold text-white">
              {signedIn ? customer?.customer_access_id || "—" : "Admin-generated credentials"}
            </div>
            <div className="mt-2 text-xs leading-6 text-white/72">
              {signedIn ? "Your customer identity is active." : "Customer login is generated from the admin panel."}
            </div>
          </div>
          <div className="metric-tile !bg-white/8 !border-white/10 text-white">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/56">Orders</div>
            <div className="mt-3 text-lg font-bold text-white">{signedIn ? orders.length : "Private"}</div>
            <div className="mt-2 text-xs leading-6 text-white/72">
              {signedIn ? "Only your linked order records." : "Buyer order visibility appears after sign-in."}
            </div>
          </div>
          <div className="metric-tile !bg-white/8 !border-white/10 text-white">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/56">Shipments</div>
            <div className="mt-3 text-lg font-bold text-white">{signedIn ? shipments.length : "Private"}</div>
            <div className="mt-2 text-xs leading-6 text-white/72">
              {signedIn ? "Your own shipment records and milestones." : "Shipment visibility appears after sign-in."}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PortalEstimator({ signedIn, isZh }) {
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState({
    unitPrice: "24",
    quantity: "120",
    unitWeight: "0.45",
    shippingMode: "sea",
    originCountry: "china",
    destinationPort: "chittagong",
    hsCode: "surgical",
    insuranceEnabled: true,
  });

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const summary = useMemo(() => {
    const unitPrice = parsePositive(form.unitPrice);
    const quantity = parsePositive(form.quantity);
    const unitWeight = parsePositive(form.unitWeight);
    const shippingMode = SHIPPING_MODES[form.shippingMode] || SHIPPING_MODES.sea;
    const origin = ORIGIN_COUNTRIES[form.originCountry] || ORIGIN_COUNTRIES.china;
    const destination = DESTINATION_PORTS[form.destinationPort] || DESTINATION_PORTS.chittagong;
    const hsProfile = HS_CODE_PROFILES[form.hsCode] || HS_CODE_PROFILES.surgical;

    const goodsValue = unitPrice * quantity;
    const totalWeight = quantity * unitWeight;
    const freightBase = Math.max(shippingMode.minimum, totalWeight * shippingMode.ratePerKg);
    const freightCharges = freightBase * origin.multiplier * destination.multiplier + shippingMode.serviceFee;
    const insurancePremium = form.insuranceEnabled ? goodsValue * 0.015 : 0;
    const localPortCharges = destination.localCharge + origin.exportDocs;
    const customsBase = goodsValue + freightCharges + insurancePremium;
    const customsDuties = customsBase * (hsProfile.dutyRate / 100);
    const taxes = (customsBase + customsDuties) * (hsProfile.taxRate / 100);
    const landedTotal =
      goodsValue + freightCharges + insurancePremium + localPortCharges + customsDuties + taxes;

    return {
      goodsValue,
      totalWeight,
      freightCharges,
      insurancePremium,
      localPortCharges,
      customsDuties,
      taxes,
      landedTotal,
      landedPerUnit: quantity ? landedTotal / quantity : 0,
      eta: shippingMode.eta,
      hsProfile,
      origin,
      destination,
      shippingMode,
    };
  }, [form]);

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1.08fr)_minmax(340px,0.92fr)]">
      <div className="surface-card rounded-[30px] border border-white/60 bg-white/90 p-6 shadow-[0_30px_80px_-46px_rgba(32,28,60,0.32)] backdrop-blur sm:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[rgba(89,45,138,0.08)] bg-[var(--rsi-purple-tint)] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--rsi-purple)]">
              <FileText className="h-3.5 w-3.5" />
              Landed cost planner
            </div>
            <h2 className="mt-4 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
              {isZh ? "按贸易报价思路查看产品、运费、关税与税费" : "Estimate landed cost like a professional freight quote."}
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-[var(--rsi-text-soft)] sm:text-base">
              {signedIn
                ? "Select origin, destination port, shipping mode, and HS code profile to see an itemized landed-cost estimate before you request the final commercial invoice."
                : "Use the planner before sign-in to preview how FOB value, freight, insurance, port clearing, duty, and tax can shape the total landed cost."}
            </p>
          </div>
          <div className="rounded-[24px] border border-[rgba(89,45,138,0.08)] bg-[linear-gradient(180deg,#faf7ff,#f2ebfb)] px-4 py-4 text-sm text-[var(--rsi-purple)] shadow-[0_18px_40px_-30px_rgba(89,45,138,0.3)]">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--rsi-purple)]/70">
              Currency view
            </div>
            <div className="mt-3 flex items-center gap-2">
              {Object.keys(CURRENCY_RATES).map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCurrency(item)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                    currency === item
                      ? "bg-[var(--rsi-purple)] text-white"
                      : "bg-white text-[var(--rsi-purple)] shadow-sm"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <div className="grid gap-2">
            <Label htmlFor="portal-unit-price">FOB unit price (USD)</Label>
            <Input
              id="portal-unit-price"
              inputMode="decimal"
              value={form.unitPrice}
              onChange={(event) => updateField("unitPrice", event.target.value)}
              className="mt-1.5 h-11 rounded-2xl border-[rgba(89,45,138,0.12)] bg-white"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="portal-quantity">Quantity</Label>
            <Input
              id="portal-quantity"
              inputMode="numeric"
              value={form.quantity}
              onChange={(event) => updateField("quantity", event.target.value)}
              className="mt-1.5 h-11 rounded-2xl border-[rgba(89,45,138,0.12)] bg-white"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="portal-weight">Unit weight (kg)</Label>
            <Input
              id="portal-weight"
              inputMode="decimal"
              value={form.unitWeight}
              onChange={(event) => updateField("unitWeight", event.target.value)}
              className="mt-1.5 h-11 rounded-2xl border-[rgba(89,45,138,0.12)] bg-white"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="portal-origin">Country of origin</Label>
            <select
              id="portal-origin"
              value={form.originCountry}
              onChange={(event) => updateField("originCountry", event.target.value)}
              className="mt-1.5 h-11 w-full rounded-2xl border border-[rgba(89,45,138,0.12)] bg-white px-3 text-sm shadow-sm outline-none transition focus:border-[var(--rsi-purple)] focus:ring-2 focus:ring-[var(--rsi-purple)]/10"
            >
              {Object.entries(ORIGIN_COUNTRIES).map(([value, item]) => (
                <option key={value} value={value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="portal-destination-port">Port of destination</Label>
            <select
              id="portal-destination-port"
              value={form.destinationPort}
              onChange={(event) => updateField("destinationPort", event.target.value)}
              className="mt-1.5 h-11 w-full rounded-2xl border border-[rgba(89,45,138,0.12)] bg-white px-3 text-sm shadow-sm outline-none transition focus:border-[var(--rsi-purple)] focus:ring-2 focus:ring-[var(--rsi-purple)]/10"
            >
              {Object.entries(DESTINATION_PORTS).map(([value, item]) => (
                <option key={value} value={value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="portal-shipping-mode">Shipping mode</Label>
            <select
              id="portal-shipping-mode"
              value={form.shippingMode}
              onChange={(event) => updateField("shippingMode", event.target.value)}
              className="mt-1.5 h-11 w-full rounded-2xl border border-[rgba(89,45,138,0.12)] bg-white px-3 text-sm shadow-sm outline-none transition focus:border-[var(--rsi-purple)] focus:ring-2 focus:ring-[var(--rsi-purple)]/10"
            >
              {Object.entries(SHIPPING_MODES).map(([value, item]) => (
                <option key={value} value={value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2 sm:col-span-2 xl:col-span-2">
            <Label htmlFor="portal-hs-code">HS code profile</Label>
            <select
              id="portal-hs-code"
              value={form.hsCode}
              onChange={(event) => updateField("hsCode", event.target.value)}
              className="mt-1.5 h-11 w-full rounded-2xl border border-[rgba(89,45,138,0.12)] bg-white px-3 text-sm shadow-sm outline-none transition focus:border-[var(--rsi-purple)] focus:ring-2 focus:ring-[var(--rsi-purple)]/10"
            >
              {Object.entries(HS_CODE_PROFILES).map(([value, item]) => (
                <option key={value} value={value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="portal-insurance-toggle">Insurance</Label>
            <button
              id="portal-insurance-toggle"
              type="button"
              onClick={() => updateField("insuranceEnabled", !form.insuranceEnabled)}
              className={`mt-1.5 h-11 rounded-2xl border px-4 text-sm font-semibold transition ${
                form.insuranceEnabled
                  ? "border-[var(--rsi-purple)] bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)]"
                  : "border-[rgba(89,45,138,0.12)] bg-white text-gray-600"
              }`}
            >
              {form.insuranceEnabled ? "Insurance on" : "Insurance off"}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[30px] border border-[rgba(16,23,44,0.06)] bg-[linear-gradient(160deg,#10172c_0%,#241139_100%)] p-6 text-white shadow-[0_34px_90px_-50px_rgba(16,23,44,0.9)] sm:p-7">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/66">
              Approximate landed cost
            </div>
            <div className="mt-4 font-display text-4xl font-black tracking-tight">
              {formatMoney(convertMoney(summary.landedTotal, currency), currency)}
            </div>
            <div className="mt-2 text-sm text-white/72">
              Approx. {formatMoney(convertMoney(summary.landedPerUnit, currency), currency)} per unit
            </div>
          </div>
          <div className="rounded-2xl border border-white/12 bg-white/10 px-4 py-3 text-right">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/60">Transit view</div>
            <div className="mt-2 font-semibold">{summary.shippingMode.label}</div>
            <div className="mt-1 text-xs text-white/66">{summary.eta}</div>
          </div>
        </div>

        <div className="mt-6 rounded-[26px] border border-white/10 bg-white/6 p-4">
          <div className="grid gap-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-white/68">FOB price</span>
              <span className="font-semibold text-white">{formatMoney(convertMoney(summary.goodsValue, currency), currency)}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-white/68">Freight charges</span>
              <span className="font-semibold text-white">{formatMoney(convertMoney(summary.freightCharges, currency), currency)}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-white/68">Customs duties & taxes</span>
              <span className="font-semibold text-white">
                {formatMoney(convertMoney(summary.customsDuties + summary.taxes, currency), currency)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-white/68">Insurance premium</span>
              <span className="font-semibold text-white">
                {formatMoney(convertMoney(summary.insurancePremium, currency), currency)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-white/68">Local port & clearing charges</span>
              <span className="font-semibold text-white">
                {formatMoney(convertMoney(summary.localPortCharges, currency), currency)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-3">
          <div className="rounded-2xl border border-white/10 bg-white/6 px-4 py-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/56">Route</div>
            <div className="mt-2 text-sm text-white">
              {summary.origin.label} to {summary.destination.label}
            </div>
            <div className="mt-1 text-xs leading-6 text-white/56">
              {summary.destination.market} import lane via {summary.shippingMode.label.toLowerCase()}.
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/6 px-4 py-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-white/56">HS code profile</div>
            <div className="mt-2 text-sm text-white">{summary.hsProfile.label}</div>
            <div className="mt-1 text-xs leading-6 text-white/56">
              Duty {summary.hsProfile.dutyRate}% · Tax/VAT {summary.hsProfile.taxRate}% · Total cargo {summary.totalWeight.toFixed(2)} kg
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-[24px] border border-white/10 bg-white/8 px-4 py-4 text-xs leading-6 text-white/64">
          These figures are planning estimates only. Actual freight, local charges, insurance, duty, and tax depend on product specification, the final HS classification, destination rules, and the final commercial quotation.
        </div>
      </div>
    </div>
  );
}

function ShipmentMilestones({ shipment }) {
  const activeIndex = shipmentStepIndex(shipment);

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
      {MILESTONE_STEPS.map((step, index) => {
        const isDone = index < activeIndex;
        const isActive = index === activeIndex;
        return (
          <div
            key={step.key}
            className={`rounded-2xl border px-3 py-3 text-xs transition ${
              isDone || isActive
                ? "border-[var(--rsi-purple)]/20 bg-[var(--rsi-purple-tint)] text-[var(--rsi-navy)]"
                : "border-gray-200 bg-white text-gray-400"
            }`}
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.16em]">
              {String(index + 1).padStart(2, "0")}
            </div>
            <div className="mt-2 font-semibold">{step.label}</div>
            <div className="mt-1 text-[11px] leading-5">
              {isActive ? "Current stage" : isDone ? "Completed" : "Upcoming"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ShipmentMetaField({ label, value, copyLabel }) {
  return (
    <div className="rounded-2xl border border-gray-100 bg-[var(--rsi-bg-subtle)] px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-gray-400">{label}</div>
          <div className="mt-1 text-sm font-medium text-[var(--rsi-navy)]">{value || "Awaiting issue"}</div>
        </div>
        {value ? (
          <button
            type="button"
            onClick={() => copyField(value, copyLabel || label)}
            className="rounded-full border border-gray-200 p-2 text-gray-500 transition hover:text-[var(--rsi-purple)]"
            aria-label={`Copy ${label}`}
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ShipmentCard({ shipment, navigate }) {
  return (
    <div className="rounded-[30px] border border-gray-100 bg-white p-5 shadow-[0_18px_50px_-38px_rgba(42,19,65,0.28)] sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Shipment record</div>
          <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
            {shipment.tracking_id || "Tracking pending"}
          </div>
          <div className="mt-2 text-sm text-[var(--rsi-text-soft)]">
            {shipment.product_summary || shipment.current_location || shipment.destination || "Linked shipment record"}
          </div>
        </div>
        <div className="text-right">
          <div className="inline-flex rounded-full bg-sky-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-sky-700">
            {STATUS_LABEL[shipment.current_status] || shipment.current_status || "active"}
          </div>
          <div className="mt-2 text-xs text-gray-500">{formatDate(shipment.updated_at)}</div>
        </div>
      </div>

      <div className="mt-5">
        <ShipmentMilestones shipment={shipment} />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <ShipmentMetaField label="B/L / AWB Number" value={shipment.reference_no} copyLabel="B/L / AWB Number" />
        <ShipmentMetaField label="Container Number" value={shipment.container_no} copyLabel="Container Number" />
        <ShipmentMetaField label="ETA" value={etaForShipment(shipment)} copyLabel="ETA" />
        <ShipmentMetaField label="ETD" value={formatDateShort(etdForShipment(shipment))} copyLabel="ETD" />
        <ShipmentMetaField label="Origin" value={shipment.origin} copyLabel="Origin" />
        <ShipmentMetaField label="Destination" value={shipment.destination} copyLabel="Destination" />
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => navigate(`/track?id=${encodeURIComponent(shipment.tracking_id)}`)}
          className="btn-ghost !px-4 !py-2.5 text-xs"
        >
          Open live tracking <ChevronRight className="h-4 w-4" />
        </button>
        <div className="rounded-full border border-gray-200 bg-[var(--rsi-bg-subtle)] px-3 py-2 text-xs text-gray-500">
          {shipment.vessel_or_flight || shipment.carrier || "Carrier details pending"}
        </div>
      </div>
    </div>
  );
}

function ShipmentPreviewEmpty() {
  return (
    <div className="rounded-[30px] border border-dashed border-gray-200 bg-[linear-gradient(180deg,#fbfbfd,#f7f3fb)] p-6 sm:p-7">
      <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Expected shipment layout</div>
      <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
        Your milestone tracker will appear here after shipment linkage.
      </div>
      <div className="mt-3 text-sm leading-7 text-[var(--rsi-text-soft)]">
        Instead of an empty portal, this section now shows the active logistics structure buyers will use once a shipment is linked from the admin panel.
      </div>
      <div className="mt-5">
        <ShipmentMilestones shipment={{ current_status: "processing" }} />
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <ShipmentMetaField label="B/L / AWB Number" value="" />
        <ShipmentMetaField label="Container Number" value="" />
        <ShipmentMetaField label="ETA" value="" />
        <ShipmentMetaField label="ETD" value="" />
        <ShipmentMetaField label="Origin" value="" />
        <ShipmentMetaField label="Destination" value="" />
      </div>
    </div>
  );
}

function TradeDocumentCenter({ orders, shipments, supportEmail, isZh }) {
  const firstOrder = orders[0];
  const firstShipment = shipments[0];
  const contactSubjectBase = firstOrder?.order_number || firstShipment?.tracking_id || "Client Portal Request";
  const requestLink = (docName) =>
    `mailto:${supportEmail}?subject=${encodeURIComponent(`${docName} request - ${contactSubjectBase}`)}`;

  return (
    <div className="surface-card rounded-[30px] p-6 sm:p-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Trade Document Center</div>
          <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
            Request the customer documents your team needs.
          </div>
        </div>
        <div className="rounded-full border border-[rgba(89,45,138,0.08)] bg-[var(--rsi-purple-tint)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--rsi-purple)]">
          {isZh ? "文档中心" : "Docs"}
        </div>
      </div>

      <div className="mt-5 grid gap-4 lg:grid-cols-2">
        <a href={requestLink("Commercial Invoice")} className="rounded-3xl border border-gray-100 bg-[var(--rsi-bg-subtle)] p-4 transition hover:-translate-y-0.5 hover:shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Proforma / Commercial Invoice</div>
          <div className="mt-2 font-semibold text-[var(--rsi-navy)]">Request CI by email</div>
          <div className="mt-2 text-sm text-gray-600">The trade desk will email the customer-linked invoice package when it is ready or when your team requests it.</div>
        </a>
        <a href={requestLink("Packing List")} className="rounded-3xl border border-gray-100 bg-[var(--rsi-bg-subtle)] p-4 transition hover:-translate-y-0.5 hover:shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Packing List</div>
          <div className="mt-2 font-semibold text-[var(--rsi-navy)]">Request PL by email</div>
          <div className="mt-2 text-sm text-gray-600">Packing lists are shared directly with the customer by email whenever needed for customs preparation or warehouse checks.</div>
        </a>
        <a href={requestLink("Certificate of Origin")} className="rounded-3xl border border-gray-100 bg-[var(--rsi-bg-subtle)] p-4 transition hover:-translate-y-0.5 hover:shadow-sm">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Certificate of Origin</div>
          <div className="mt-2 font-semibold text-[var(--rsi-navy)]">Request C/O by email</div>
          <div className="mt-2 text-sm text-gray-600">Origin certificates are issued to the customer through the trade desk whenever they are required for clearance or duty support.</div>
        </a>
        <div className="rounded-3xl border border-gray-100 bg-[var(--rsi-bg-subtle)] p-4">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Customer document policy</div>
          <div className="mt-2 font-semibold text-[var(--rsi-navy)]">No public company license downloads</div>
          <div className="mt-2 text-sm text-gray-600">
            Customers do not download company license files from this portal. Only customer-specific documents shared through email or the customer document feed are provided here.
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CustomerPortal() {
  const navigate = useNavigate();
  const customerSession = useCustomerAppSession();
  const { language, setLanguage, isZh } = useSiteLanguage();
  const customer = customerSession.session?.customer || null;
  const orders = useMemo(
    () => customerSession.session?.orders || [],
    [customerSession.session?.orders]
  );
  const shipments = useMemo(
    () => customerSession.session?.shipments || [],
    [customerSession.session?.shipments]
  );

  const { data: settings } = usePublicSettings();

  const alertCount = useMemo(
    () =>
      shipments.filter((item) =>
        ["processing", "in_transit", "customs", "delayed", "out_for_delivery"].includes(item.current_status)
      ).length || 1,
    [shipments]
  );

  if (customerSession.loading && !customer) {
    return (
      <section className="bg-[var(--rsi-bg-subtle)] py-16">
        <div className="container-rsi">
          <div className="surface-card rounded-[32px] p-10 text-center">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-[var(--rsi-purple-tint)] grid place-items-center text-[var(--rsi-purple)]">
              <Clock3 className="h-6 w-6 animate-pulse" />
            </div>
            <div className="mt-5 font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)]">
              Opening your client portal
            </div>
            <div className="mt-3 text-sm text-gray-600">
              Your customer workspace is loading.
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!customerSession.isAuthenticated) {
    return (
      <section className="bg-[var(--rsi-bg-subtle)] py-12 md:py-16">
        <div className="container-rsi space-y-6">
          <PortalHero
            signedIn={false}
            customer={null}
            orders={[]}
            shipments={[]}
            language={language}
            setLanguage={setLanguage}
            alertCount={1}
          />
          <PortalEstimator signedIn={false} isZh={isZh} />
          <div id="portal-shipments" className="surface-card rounded-[30px] p-6 sm:p-7">
            <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Shipment workflow preview</div>
            <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
              Buyers will see a real logistics timeline here after sign-in.
            </div>
            <div className="mt-3 text-sm leading-7 text-[var(--rsi-text-soft)]">
              This section is prepared for active shipment visibility, with milestones, ETA/ETD, and copyable trade references.
            </div>
            <div className="mt-5">
              <ShipmentPreviewEmpty />
            </div>
          </div>
          <TradeDocumentCenter
            orders={[]}
            shipments={[]}
            supportEmail={settings?.email || "info@rsintl23.com"}
            isZh={isZh}
          />
        </div>
      </section>
    );
  }

  return (
    <section className="bg-[var(--rsi-bg-subtle)] py-12 md:py-16" data-testid="customer-portal-page">
      <div className="container-rsi space-y-6">
        <PortalHero
          signedIn
          customer={customer}
          orders={orders}
          shipments={shipments}
          onLogout={customerSession.logout}
          onOpenMarketplace={() => navigate("/app")}
          language={language}
          setLanguage={setLanguage}
          alertCount={alertCount}
        />
        <PortalEstimator signedIn isZh={isZh} />

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)]">
          <div className="space-y-6">
            <div className="surface-card rounded-[30px] p-6 sm:p-7">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Recent orders</div>
                  <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                    Order visibility for this buyer
                  </div>
                </div>
                <Link to="/app" className="btn-ghost !px-4 !py-2.5 text-xs">
                  View marketplace
                </Link>
              </div>
              <div className="mt-5 space-y-3">
                {orders.length ? (
                  orders.slice(0, 6).map((order) => (
                    <div key={order.id} className="rounded-3xl border border-gray-100 bg-[var(--rsi-bg-subtle)] px-4 py-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="font-semibold text-[var(--rsi-navy)]">{order.order_number}</div>
                          <div className="mt-1 text-sm text-gray-600">
                            {order.total_line_items} lines · {order.total_units} units
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="inline-flex rounded-full bg-[var(--rsi-purple-tint)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[var(--rsi-purple)]">
                            {String(order.status || "").replaceAll("_", " ")}
                          </div>
                          <div className="mt-2 text-xs text-gray-500">{formatDate(order.created_at)}</div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="rounded-3xl border border-dashed border-gray-200 p-6 text-sm text-gray-500">
                    No linked customer orders are available yet inside this portal.
                  </div>
                )}
              </div>
            </div>

            <div id="portal-shipments" className="surface-card rounded-[30px] p-6 sm:p-7">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Shipment records</div>
              <div className="mt-2 font-display text-2xl font-black tracking-tight text-[var(--rsi-navy)]">
                Active logistics visibility for this customer
              </div>
              <div className="mt-3 text-sm leading-7 text-[var(--rsi-text-soft)]">
                Every shipment card now shows milestone progress, trade references, and estimated movement windows in a more professional trade-facing layout.
              </div>
              <div className="mt-5 space-y-4">
                {shipments.length ? (
                  shipments.slice(0, 6).map((shipment) => (
                    <ShipmentCard key={shipment.id} shipment={shipment} navigate={navigate} />
                  ))
                ) : (
                  <ShipmentPreviewEmpty />
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <TradeDocumentCenter
              orders={orders}
              shipments={shipments}
              supportEmail={settings?.email || "info@rsintl23.com"}
              isZh={isZh}
            />

            <div className="surface-card rounded-[30px] p-6 sm:p-7">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Portal identity</div>
              <div className="mt-4 flex items-center gap-4">
                <div className="grid h-14 w-14 place-items-center rounded-2xl bg-[var(--rsi-purple-tint)] text-[var(--rsi-purple)]">
                  <UserRound className="h-6 w-6" />
                </div>
                <div>
                  <div className="font-semibold text-[var(--rsi-navy)]">{customer?.customer_name || "Customer"}</div>
                  <div className="text-sm text-gray-600">{customer?.company_name || "Client profile linked"}</div>
                </div>
              </div>
              <div className="mt-5 space-y-3 text-sm">
                <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Access ID</div>
                  <div className="mt-1 font-mono-plex font-semibold text-[var(--rsi-navy)]">
                    {customer?.customer_access_id || "—"}
                  </div>
                </div>
                <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Email</div>
                  <div className="mt-1 text-[var(--rsi-navy)]">{customer?.email || "Not provided"}</div>
                </div>
                <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Phone</div>
                  <div className="mt-1 text-[var(--rsi-navy)]">{customer?.phone || "Not provided"}</div>
                </div>
                <div className="rounded-2xl bg-[var(--rsi-bg-subtle)] px-4 py-3">
                  <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Planning guidance</div>
                  <div className="mt-1 text-[var(--rsi-navy)]">
                    Use the landed cost planner above for a freight-style estimate of FOB value, freight, insurance, local charges, duty, and tax before the final invoice is issued.
                  </div>
                </div>
              </div>
            </div>

            <div className="surface-card rounded-[30px] p-6 sm:p-7">
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400">Portal tools</div>
              <div className="mt-4 space-y-3">
                <button
                  type="button"
                  onClick={() => navigate("/track")}
                  className="w-full rounded-2xl border border-gray-100 bg-[var(--rsi-bg-subtle)] px-4 py-4 text-left transition hover:-translate-y-0.5 hover:shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white text-[var(--rsi-purple)] shadow-sm">
                      <Truck className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-semibold text-[var(--rsi-navy)]">Shipment tracking</div>
                      <div className="mt-1 text-xs text-gray-500">Open public shipment timeline tools.</div>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/app")}
                  className="w-full rounded-2xl border border-gray-100 bg-[var(--rsi-bg-subtle)] px-4 py-4 text-left transition hover:-translate-y-0.5 hover:shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white text-[var(--rsi-purple)] shadow-sm">
                      <Package2 className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-semibold text-[var(--rsi-navy)]">Marketplace access</div>
                      <div className="mt-1 text-xs text-gray-500">Return to the app marketplace and product area.</div>
                    </div>
                  </div>
                </button>
                <Link
                  to="/contact"
                  className="block w-full rounded-2xl border border-gray-100 bg-[var(--rsi-bg-subtle)] px-4 py-4 text-left transition hover:-translate-y-0.5 hover:shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-2xl bg-white text-[var(--rsi-purple)] shadow-sm">
                      <ClipboardList className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-semibold text-[var(--rsi-navy)]">Invoice and reorder help</div>
                      <div className="mt-1 text-xs text-gray-500">Contact the trade desk for invoice coordination, reorders, and destination support.</div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
